import { useState } from 'react'
import EstadoAsistencia from '../../../../../components/EstadoAsistencia/EstadoAsistencia'
import PageLoading from '../../../../../components/PageLoading/PageLoading'
import { useNotification } from '../../../../../context/NotificationManager/NotificationManager'
import {
  getAsistenciasSinProfesor,
  updateAsistencia
} from '../../../../../utils/api/asistencias'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dateToString, monthToString } from '../../../../../utils/date'
import { downloadCSVFile, getCSVContent } from '../../../../../utils/csv'
import { dateTimeSort, textFilter } from '../../../../../utils/table'
import TableHeader from '../../../../../components/TableHeader/TableHeader'
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Typography
} from '@mui/material'
import InformationTable from '../../../../../components/Table/InformationTable'
import MesRecienteSelector from '../../../../../components/form/MesRecienteSelector/MesRecienteSelector'
import ProfesorSelector from '../../../../../components/form/ProfesorSelector/ProfesorSelector'
import css from './Asistencias.module.css'

const Asistencias = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()

  const [mes, setMes] = useState(monthToString(new Date()))
  const [search, setSearch] = useState('')
  const [mostrarClasesImpartidas, setMostrarClasesImpartidas] = useState(false)

  const { isLoading, data: asistenciaList = [] } = useQuery({
    queryKey: ['planificacion', 'asistencia', 'list', 'sin-profesor', mes],
    queryFn: () =>
      getAsistenciasSinProfesor(mes)
        .then(datos =>
          datos.map(asistencia => ({
            ...asistencia,
            fecha: dateToString(asistencia.fecha)
          }))
        )
        .catch(err => {
          notification.error({
            title: 'Error al recuperar las asistencias',
            content: err
          })
          return []
        })
  })

  const { isPending: isUpdatingAsistencia, mutate: modificarAsistencia } =
    useMutation({
      mutationFn: updateAsistencia,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['planificacion', 'asistencia', 'list']
        })
      },
      onError: err => {
        notification.error({
          title: 'Error al actualizar la asistencia',
          content: err
        })
      }
    })

  const asistenciaListFiltrada = asistenciaList.filter(
    asistencia =>
      !mostrarClasesImpartidas ||
      (asistencia.asistencia === 'VIENE' && !asistencia.profesorId)
  )

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Alumno',
        'Centro',
        'Fecha',
        'Hora',
        'Asignatura',
        'Clase',
        'Profesor',
        'Observaciones'
      ],
      data: asistenciaListFiltrada,
      transform: c => [
        c.alumno,
        c.centro,
        c.fecha,
        c.hora,
        `${c.asignatura} - ${c.nivel}`,
        c.clase,
        c.profesor,
        c.observaciones
      ]
    })
    downloadCSVFile(content, `asistencias-pendientes`)
  }

  const filterSearch = asistencia => {
    return textFilter({
      object: asistencia,
      fields: [
        'alumno',
        'centro',
        'fecha',
        'hora',
        'asignatura',
        'tipo',
        'nivel',
        'clase',
        'profesor',
        'observaciones'
      ],
      search: search
    })
  }

  const handleProfesorChange = (profesor, asistencia) => {
    if (isUpdatingAsistencia) return
    modificarAsistencia({
      id: asistencia.id,
      profesor: profesor.id
    })
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
            <MesRecienteSelector
              className={css.mes}
              name='mes'
              label='Mes'
              value={mes}
              forwardOptions={1}
              backwardOptions={18}
              onChange={e => setMes(e.target.value)}
            />
            <FormControlLabel
              className={css.checkboxLabel}
              control={
                <Checkbox
                  checked={mostrarClasesImpartidas}
                  onChange={e => setMostrarClasesImpartidas(e.target.checked)}
                />
              }
              label='Mostrar clases impartidas'
            />
          </div>
        }
        title={`${asistenciaListFiltrada.length} asistencias sin profesor`}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      {asistenciaListFiltrada.length === 0 && (
        <div className={css.empty}>
          <Typography className={css.placeholder} variant='body1'>
            No hay asistencias sin asignar profesor
          </Typography>
        </div>
      )}
      {asistenciaListFiltrada.length > 0 && (
        <InformationTable
          details={[
            { title: 'Alumno', key: 'alumno', sortKey: 'alumnoNombre' },
            { title: 'Fecha', key: 'fecha', sortFunction: dateTimeSort },
            { title: 'Asignatura', key: 'asignatura' },
            { title: 'Profesor', key: 'profesor', sortDisabled: true },
            {
              title: 'Asistencia',
              key: 'asistencia',
              align: 'center',
              sortDisabled: true
            },
            { title: 'Observaciones', key: 'observaciones', sortDisabled: true }
          ]}
          data={asistenciaListFiltrada.filter(filterSearch).map(asistencia => ({
            key: asistencia.id,
            alumno: (
              <Link
                className={css.link}
                href={`/alumnos/${asistencia.alumnoId}/ficha`}
              >
                {asistencia.alumno}
              </Link>
            ),
            alumnoNombre: asistencia.alumno,
            fecha: `${asistencia.fecha} ${asistencia.hora}`,
            asignatura: asistencia.asignatura,
            profesor: (
              <ProfesorSelector
                className={css.input}
                name='profesor'
                label='Profesor'
                value={asistencia.profesorId}
                onChange={(_e, value) =>
                  handleProfesorChange(value, asistencia)
                }
              />
            ),
            asistencia: <EstadoAsistencia asistencia={asistencia} />,
            observaciones: asistencia.observaciones
          }))}
        />
      )}
    </PageLoading>
  )
}

export default Asistencias
